import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {StorageTypeUpdated} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';

import {
    StorageType,
    StorageTypeOnServerCreated,
    selectStorageTypesActionLoading,
    selectStorageTypeById,
    selectLastCreatedStorageTypeId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';
import {combineLatest} from 'rxjs'

@Component({
    selector: 'kt-storage-type-edit',
    templateUrl: './storage-type-edit.component.html',
    styleUrls: ['./storage-type-edit.component.scss']
})


export class StorageTypeEditComponent implements OnInit, OnDestroy {

    model: StorageType;
    oldModel: StorageType;

    storageTypeForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    readonly = false
    propertySubTypeId = 0;
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param subheaderService: SubheaderService
     * @param store: Store<AppState>
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                public subheaderService: SubheaderService,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectStorageTypesActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            this.readonly = data.readonly
            if (params.property_sub_type_id) {
                this.propertySubTypeId = Number(params.property_sub_type_id);
            }
            const id = params.id;
            if (id && id > 0) {
                this.store.pipe(select(selectStorageTypeById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initStorageType();
                    }
                });
            } else {
                this.model = new StorageType();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initStorageType();
            }
        });
        this.subscriptions.push(routeSubscription);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initStorageType() {
        this.createForm();
        // set subheaderService
    }


    createForm() {
        this.storageTypeForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return `Edit Storage Type '${
                this.model.name
                }'`;
        }

        return 'New Storage Type';
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.storageTypeForm.controls[controlName];
        const result = control.invalid && control.touched;
        return result;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.storageTypeForm.markAsPristine();
        this.storageTypeForm.markAsUntouched();
        this.storageTypeForm.updateValueAndValidity();
    }

    prepareStorageType(): StorageType {
        const controls = this.storageTypeForm.controls;
        const _model = new StorageType();
        _model.clear();
        _model.id = this.model.id;
        _model.name = controls.name.value;
        _model.property_sub_type_id = this.propertySubTypeId;
        _model.is_hidden = this.model.is_hidden

        return _model;
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.storageTypeForm.controls;
        /** check form */
        if (this.storageTypeForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        const editedFacility = this.prepareStorageType();
        if (this.model.id > 0) {
            this.updateStorageType(editedFacility);
        } else {
            this.createStorageType(editedFacility);
        }
    }

    /**
     * Update model
     *
     * @param _model: StorageTypeModel
     */
    updateStorageType(_model: StorageType) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateStorageType: Update<StorageType> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new StorageTypeUpdated({
            partialStorageType: updateStorageType,
            storageType: _model
        }));
        this.oldModel = _model;
        this.navigateToParent();

    }

    /**
     * Create model
     *
     * @param _model: StorageTypeModel
     */
    createStorageType(_model: StorageType) {
        this.store.dispatch(new StorageTypeOnServerCreated({storageType: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedStorageTypeId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }

}
